import React, { useEffect, useRef, useState } from 'react';

// Assets
import processingFile from '@/images/design-system-v2/loading-state/processing-file.gif';

// Components
import ProgressBar from '@/views/components/common/ProgressBar';
import { FormattedMessageV2 } from '@/views/components/i18n';

enum PROCESSING_FILE_STEP {
  DIGGING = 'digging',
  FINDING = 'finding',
  PROCESSING = 'processing',
}

type Props = {
  fileSize?: number;
  isProcessing: boolean;
  onEndEstimatedProcessing?: () => void;
};

const ProcessingFile: React.FC<Props> = ({ fileSize, isProcessing, onEndEstimatedProcessing }) => {
  const intervalRef = useRef<number>();
  const [currentStep, setCurrentStep] = useState(PROCESSING_FILE_STEP.PROCESSING);
  const [progress, setProgress] = useState<number>(0);

  const bigFileSize = 3_000_000; // File size in bytes (3 MB)
  const defaultSize = 1_000_000; // File size in bytes (1 MB)
  const diggingMinLimit = 85;
  const findingMinLimit = 10;
  const totalLimit = 100;

  const i18nBasePath = 'ProcessingFile';

  useEffect(() => {
    if (isProcessing) {
      simulateProgress(fileSize ?? defaultSize);
    }
    if (!isProcessing && progress > findingMinLimit && progress < diggingMinLimit) {
      setProgress(completeProcessing(totalLimit));
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  const calculateProgress = (prevProgress: number, size: number): number => {
    const factor = defineFactorBySize(size);
    const currProgress = Math.min(prevProgress + Math.floor(Math.random() * 100) * factor, 100);
    const isProcessingStep = prevProgress < findingMinLimit && currentStep !== PROCESSING_FILE_STEP.PROCESSING;
    const isFindingStep = prevProgress >= findingMinLimit && prevProgress < diggingMinLimit && currentStep !== PROCESSING_FILE_STEP.FINDING;
    const isDiggingStep = prevProgress >= diggingMinLimit && currentStep !== PROCESSING_FILE_STEP.DIGGING;

    if (isProcessingStep) setCurrentStep(PROCESSING_FILE_STEP.PROCESSING);
    if (isFindingStep) setCurrentStep(PROCESSING_FILE_STEP.FINDING);
    if (isDiggingStep) setCurrentStep(PROCESSING_FILE_STEP.DIGGING);

    return Number((Math.round(currProgress * 100) / 100).toFixed(0));
  };

  const completeProcessing = (prevProgress: number): number => {
    clearInterval(intervalRef.current);
    onEndEstimatedProcessing && onEndEstimatedProcessing();
    return prevProgress;
  };

  const defineFactorBySize = (size: number): number => {
    if (size >= bigFileSize) return (1 / size) * 30_000;
    if (size >= defaultSize) return (1 / size) * 45_000;
    return 0.05;
  };

  const simulateProgress = (size: number): void => {
    intervalRef.current = window.setInterval(() => {
      setProgress(prevProgress => {
        if (prevProgress < totalLimit) {
          // If the progress is stuck in the server, we should just show the digging step
          if (isProcessing && prevProgress >= diggingMinLimit) {
            setCurrentStep(PROCESSING_FILE_STEP.DIGGING);
            return diggingMinLimit;
          }
          return calculateProgress(prevProgress, size);
        }
        return completeProcessing(prevProgress);
      });
    }, 50);
  };

  const renderStepText = (): JSX.Element => {
    let i18nStepPath = 'ProcessingFileStep';
    if (currentStep === PROCESSING_FILE_STEP.FINDING) i18nStepPath = 'FindingInFileStep';
    if (currentStep === PROCESSING_FILE_STEP.DIGGING) i18nStepPath = 'DiggingInFileStep';

    return (
      <>
        <FormattedMessageV2 className="title" toLocalize={`${i18nBasePath}.${i18nStepPath}.Title`} />
        <FormattedMessageV2 className="subtitle" toLocalize={`${i18nBasePath}.${i18nStepPath}.Body`} />
      </>
    );
  };

  return (
    <div className="processing-file">
      <img className="image" src={processingFile} alt="processing-file" />
      {renderStepText()}
      <ProgressBar progress={progress} />
      <FormattedMessageV2 className="disclosure" toLocalize={`${i18nBasePath}.Disclosure`} />
    </div>
  );
};

export default ProcessingFile;
